import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, Download, Search, Filter } from 'lucide-react';
import {
  Button,
  SearchBar,
  DataTable,
  LoadingSpinner,
  FileUpload
} from '../../components/common';
import { Navigation } from '../../components/layouts';
import { ClientForm } from '../../components/forms';
import { clientService } from '../../services/clientService';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../components/common/Pagination';

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [contractFilter, setContractFilter] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');

  useEffect(() => {
    loadClients();
  }, []);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch();
      } else {
        loadClients();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const clientsData = await clientService.getAll();
      setClients(clientsData || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setError('Nu s-au putut încărca clienții. Verificați conexiunea la server.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadClients();
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      const searchResults = await clientService.search(searchTerm.trim());
      setClients(searchResults || []);
    } catch (error) {
      console.error('Failed to search clients:', error);
      setError('Căutarea a eșuat. Încercați din nou.');
      setClients([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Get available counties from clients
  const availableCounties = useMemo(() => {
    const counties = [...new Set(clients.map(c => c.county).filter(Boolean))].sort();
    return counties;
  }, [clients]);

  // Filter clients based on all criteria
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Contract filter
      const matchesContractFilter = 
        contractFilter === 'all' ||
        (contractFilter === 'with-contracts' && client.contractCount > 0) ||
        (contractFilter === 'without-contracts' && client.contractCount === 0);

      // County filter
      const matchesCounty = 
        selectedCounty === 'all' || 
        client.county === selectedCounty;

      return matchesContractFilter && matchesCounty;
    });
  }, [clients, contractFilter, selectedCounty]);

  // Use pagination hook on filtered clients
  const {
    currentPage,
    pageSize,
    totalPages,
    startItem,
    endItem,
    currentItems,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    changePageSize,
    pageSizeOptions
  } = usePagination(filteredClients, {
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 25, 50, 100]
  });

  const handleClientSubmit = async (clientData) => {
    try {
      setError(null);

      if (editingClient) {
        await clientService.update(editingClient.id, clientData);
        console.log('Client updated successfully:', editingClient.id);
      } else {
        await clientService.create(clientData);
        console.log('Client created successfully');
      }

      await loadClients();
      setShowClientForm(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Failed to save client:', error);
      setError(editingClient ? 'Nu s-a putut actualiza clientul.' : 'Nu s-a putut crea clientul.');
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowClientForm(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Sunteți sigur că doriți să ștergeți acest client?')) {
      try {
        setError(null);
        await clientService.delete(clientId);
        console.log('Client deleted successfully:', clientId);
        await loadClients();
      } catch (error) {
        console.error('Failed to delete client:', error);
        setError('Nu s-a putut șterge clientul.');
      }
    }
  };

  const handleExport = async () => {
    try {
      setError(null);
      console.log('Exporting clients...');

      const blob = await clientService.exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clienti_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Export completed successfully');
    } catch (error) {
      console.error('Failed to export clients:', error);
      setError('Nu s-a putut exporta lista de clienți.');
    }
  };

  const handleImport = async (fileOrFiles) => {
    let file;

    if (!fileOrFiles) {
      setShowImportModal(false);
      return;
    }

    if (Array.isArray(fileOrFiles)) {
      if (fileOrFiles.length === 0) {
        setShowImportModal(false);
        return;
      }
      file = fileOrFiles[0];
    } else {
      file = fileOrFiles;
    }

    try {
      setError(null);
      setLoading(true);
      console.log('Importing file:', file);

      if (!(file instanceof File)) {
        setError('Fișier invalid. Vă rugăm să încercați din nou.');
        return;
      }

      const result = await clientService.importFromExcel(file);
      console.log('Import completed successfully:', result);

      await loadClients();
      setShowImportModal(false);

      if (result.success !== false) {
        const importedCount = result.importedCount || result.clients?.length || 0;
        const message = result.message || `${importedCount} clienți importați cu succes!`;
        alert(message);
      } else {
        setError(result.message || 'Import finalizat cu erori');
      }
    } catch (error) {
      console.error('Failed to import clients:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.Message ||
        error.message ||
        'Nu s-a putut importa fișierul. Verificați formatul.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      console.log('Downloading import template...');

      const blob = await clientService.getImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_import_clienti.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Template download completed');
    } catch (error) {
      console.error('Failed to download template:', error);
      setError('Nu s-a putut descărca template-ul.');
    }
  };

  const handleSort = (column, direction) => {
    setSortBy(column);
    setSortDirection(direction);

    const sortedClients = [...clients].sort((a, b) => {
      let aValue = a[column];
      let bValue = b[column];

      if (column === 'name') {
        aValue = a.name || '';
        bValue = b.name || '';
      }

      if (direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setClients(sortedClients);
  };

  const handleRowClick = (client) => {
    navigate(`/clients/${client.id}`);
  };

  const columns = [
    {
      key: 'name',
      title: 'Client',
      sortable: true,
      render: (value, client) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{client.name || 'N/A'}</div>
          <div className="text-sm text-gray-500">{client.representativeFullName || client.representativeName || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'fiscalCode',
      title: 'Cod Fiscal',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-900 font-mono">{value || 'N/A'}</span>
      )
    },
    {
      key: 'email',
      title: 'Email',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-900">{value || 'N/A'}</span>
      )
    },
    {
      key: 'locality',
      title: 'Locație',
      sortable: true,
      render: (value, client) => (
        <div className="text-sm text-gray-900">
          {client.locality && client.county
            ? `${client.locality}, ${client.county}`
            : client.locality || client.county || 'N/A'
          }
        </div>
      )
    },
    {
      key: 'contractCount',
      title: 'Contracte',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value || 0}
        </span>
      )
    },
    {
      key: 'actions',
      title: 'Acțiuni',
      sortable: false,
      render: (_, client) => (
        <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEditClient(client)}
          >
            Editează
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDeleteClient(client.id)}
          >
            Șterge
          </Button>
        </div>
      )
    }
  ];

  // Show loading state during initial load
  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Navigation
        title="Gestiune Clienți"
        subtitle={`${clients.length} clienți înregistrați în sistem`}
        actions={[
          {
            label: 'Import Excel',
            icon: Upload,
            variant: 'outline',
            onClick: () => setShowImportModal(true)
          },
          {
            label: 'Export',
            icon: Download,
            variant: 'outline',
            onClick: handleExport
          },
          {
            label: 'Client Nou',
            icon: Plus,
            variant: 'primary',
            onClick: () => {
              setEditingClient(null);
              setShowClientForm(true);
            }
          }
        ]}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-sm text-red-700">
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchBar
                placeholder="Căutați după nume, cod fiscal sau email..."
                value={searchTerm}
                onChange={setSearchTerm}
                onClear={() => setSearchTerm('')}
                loading={isSearching}
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                icon={Filter} 
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Ascunde filtre' : 'Mai multe filtre'}
              </Button>
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contracte
                  </label>
                  <select
                    value={contractFilter}
                    onChange={(e) => setContractFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Toți clienții</option>
                    <option value="with-contracts">Cu contracte</option>
                    <option value="without-contracts">Fără contracte</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Județ
                  </label>
                  <select
                    value={selectedCounty}
                    onChange={(e) => setSelectedCounty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Toate județele</option>
                    {availableCounties.map(county => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setContractFilter('all');
                      setSelectedCounty('all');
                      setSearchTerm('');
                    }}
                    className="w-full"
                  >
                    Resetează filtre
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Summary */}
      {(contractFilter !== 'all' || selectedCounty !== 'all') && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Afișare: {filteredClients.length} din {clients.length} clienți
          </span>
        </div>
      )}

      {/* Clients Table */}
      <DataTable
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="Nu au fost găsiți clienți"
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        hoverable
        showIndex={true}
        indexStart={startItem}
        indexLabel='Nr.'
        onRowClick={handleRowClick}
      />

      {filteredClients.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredClients.length}
          startItem={startItem}
          endItem={endItem}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageChange={goToPage}
          onPageSizeChange={changePageSize}
          pageSizeOptions={pageSizeOptions}
          itemName="clienți"
        />
      )}

      {/* Client Form Modal */}
      <ClientForm
        client={editingClient}
        isOpen={showClientForm}
        onClose={() => {
          setShowClientForm(false);
          setEditingClient(null);
        }}
        onSubmit={handleClientSubmit}
      />

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Import Clienți</h3>

            <div className="mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  Informații importante
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Clienții cu cod fiscal existent vor fi omiți</li>
                  <li>• Câmpurile obligatorii: Nume, Cod Fiscal</li>
                  <li>• Format acceptat: .xlsx, .csv (max 10MB)</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600 mb-2">
                Selectați un fișier Excel (.xlsx) sau CSV pentru import.
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Descărcați template-ul pentru import →
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="lg" />
                <span className="ml-3 text-gray-600">Se procesează fișierul...</span>
              </div>
            ) : (
              <FileUpload
                accept=".xlsx,.csv"
                allowedTypes={['xlsx', 'csv']}
                maxSize={10}
                multiple={false}
                onFileSelect={handleImport}
                disabled={loading}
                placeholder="Trageți fișierul aici sau faceți clic pentru a selecta"
              />
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowImportModal(false)}
                disabled={loading}
              >
                Anulează
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;